import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Award, Scale, Lock, Smartphone, Monitor, X, KeyRound } from "lucide-react";
import WertiaIntro from "@/components/WertiaIntro";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-360-pro/artifacts/t5motboh_wertia%20perfect.png";

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("de-CH"); } catch { return s; }
};

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Intro splash: disabled (was: shown once per session). User opted out for faster workflow.
  // To re-enable: replace `false` with the sessionStorage check below.
  // (() => { try { return sessionStorage.getItem("wertia_intro_seen") !== "1"; } catch { return true; } })()
  const [showIntro, setShowIntro] = useState(false);

  // step: "credentials" | "totp" | "session_limit"
  const [step, setStep] = useState("credentials");
  const [totpCode, setTotpCode] = useState("");
  const [activeSessions, setActiveSessions] = useState([]);
  const [resetLinkInfo, setResetLinkInfo] = useState(null); // dev mode

  const doLogin = async (opts = {}) => {
    setLoading(true);
    try {
      const result = await login(email, password, opts);
      if (result?.totp_required) {
        setStep("totp");
        toast.info("2FA-Code aus Authenticator-App eingeben");
      } else if (result?.session_limit) {
        setStep("session_limit");
        setActiveSessions(result.active_sessions || []);
        toast.warning(result.message);
      } else if (result?.user) {
        toast.success(t.welcome_back);
        navigate("/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail) {
        if (detail.code === "account_locked") {
          toast.error(detail.message);
          if (detail.reset_link) {
            setResetLinkInfo(detail.reset_link);
          }
        } else if (detail.code === "invalid_totp") {
          toast.error(detail.message);
        } else {
          toast.error(detail.message || "Login fehlgeschlagen");
        }
      } else {
        toast.error(detail || "Login fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmitCredentials = (e) => {
    e.preventDefault();
    doLogin();
  };
  const onSubmitTotp = (e) => {
    e.preventDefault();
    doLogin({ totp_code: totpCode.trim() });
  };

  const terminateAndLogin = (jti) => {
    doLogin({ force_terminate_jti: jti });
  };

  return (
    <>
      {showIntro && <WertiaIntro onDone={() => setShowIntro(false)} />}
      <div className="min-h-screen flex">
      {/* Left: branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-sidebar text-white flex-col p-12">
        <div className="flex items-center justify-start">
          <img src={LOGO_URL} alt="Wertia Finanz" className="w-40 lg:w-52 xl:w-64 h-auto" />
        </div>
        <div className="flex-1 flex flex-col justify-center">
          <div className="label-uppercase text-prime mb-3">Patrimonial Advisory · Switzerland</div>
          <h1 className="font-display font-extrabold text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Schweizer Sicherheit.<br />
            <span className="text-prime">Intelligente Entscheidungen.</span>
          </h1>
          <p className="mt-6 max-w-md text-sm text-white/70">
            Modernes CRM für Vermögensberater — Kundenverwaltung, Policen, offizielle Schweizer Formulare und Dokumenten-KI.
          </p>
          <div className="grid grid-cols-4 gap-3 mt-12 max-w-md">
            {[
              { icon: ShieldCheck, label: "VERTRAUEN" },
              { icon: Award, label: "KOMPETENZ" },
              { icon: Scale, label: "UNABHÄNGIGKEIT" },
              { icon: Lock, label: "SICHERHEIT" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center text-center gap-2">
                <Icon className="w-5 h-5 text-prime" strokeWidth={1.5} />
                <span className="text-[9px] text-white/50 font-semibold tracking-wider">{label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs font-mono text-white/30">
          v1.2 · AI · Claude 4.6 + GPT-5.5 · FINMA-ready · 2FA-protected
        </div>
      </div>

      {/* Right: form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background relative">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex justify-center mb-8">
            <img src={LOGO_URL} alt="Wertia Finanz" className="h-64 sm:h-80 md:h-96" />
          </div>

          {step === "credentials" && (
            <>
              <div className="label-uppercase text-prime mb-3">{t.login}</div>
              <h2 className="font-display font-extrabold text-4xl tracking-tight mb-2">{t.welcome_back}</h2>
              <p className="text-sm text-muted-foreground mb-10">Melden Sie sich in Ihrem Wertia-Workspace an.</p>

              <form onSubmit={onSubmitCredentials} className="space-y-5" data-testid="login-form">
                <div>
                  <Label className="label-uppercase text-muted-foreground">{t.email}</Label>
                  <Input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required data-testid="login-email"
                    className="rounded-md focus-visible:ring-prime focus-visible:ring-1 focus-visible:ring-offset-0 h-11 mt-2"
                  />
                </div>
                <div>
                  <Label className="label-uppercase text-muted-foreground">{t.password}</Label>
                  <Input
                    type="password" value={password} onChange={e => setPassword(e.target.value)}
                    required data-testid="login-password"
                    className="rounded-md focus-visible:ring-prime focus-visible:ring-1 focus-visible:ring-offset-0 h-11 mt-2"
                  />
                </div>
                <Button
                  type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full h-11 btn-prime gap-2"
                >
                  {loading ? "…" : t.sign_in} <ArrowRight className="w-4 h-4" />
                </Button>
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-prime" data-testid="forgot-password-link">
                    Passwort vergessen?
                  </Link>
                </div>
              </form>

              {resetLinkInfo && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700 rounded-md text-xs">
                  <div className="font-bold text-amber-700 dark:text-amber-300 mb-1">Konto vorübergehend gesperrt</div>
                  <div className="text-amber-800 dark:text-amber-200">
                    Eine E-Mail mit Reset-Link wurde gesendet. <br/>
                    <span className="text-[10px] opacity-70">Dev-Link: </span>
                    <a href={resetLinkInfo} className="text-prime underline break-all">{resetLinkInfo}</a>
                  </div>
                </div>
              )}

              <div className="mt-8 text-xs text-muted-foreground border-t border-border pt-4">
                <span className="label-uppercase">Sicherer Bereich</span>
                <div className="mt-1 opacity-80">Nur autorisierte Wertia-Mitarbeiter.</div>
              </div>
            </>
          )}

          {step === "totp" && (
            <>
              <div className="label-uppercase text-prime mb-3 flex items-center gap-2">
                <KeyRound className="w-3 h-3" /> 2-Faktor-Authentifizierung
              </div>
              <h2 className="font-display font-extrabold text-3xl tracking-tight mb-2">Code eingeben</h2>
              <p className="text-sm text-muted-foreground mb-8">
                Öffnen Sie Ihre Authenticator-App und geben Sie den 6-stelligen Code für <strong>{email}</strong> ein.
              </p>
              <form onSubmit={onSubmitTotp} className="space-y-5">
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={9}
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value)}
                  placeholder="123 456"
                  autoFocus
                  data-testid="login-totp"
                  className="h-14 text-center text-2xl tracking-[0.4em] font-mono"
                />
                <Button type="submit" disabled={loading || totpCode.length < 6} className="w-full h-11 btn-prime gap-2" data-testid="login-totp-submit">
                  {loading ? "…" : "Verifizieren"} <ArrowRight className="w-4 h-4" />
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep("credentials"); setTotpCode(""); }}
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                >
                  ← Zurück
                </button>
              </form>
              <div className="mt-4 text-[11px] text-muted-foreground text-center">
                Verloren? Verwenden Sie einen Ihrer 8-stelligen Recovery-Codes.
              </div>
            </>
          )}

          {step === "session_limit" && (
            <>
              <div className="label-uppercase text-prime mb-3">Sitzungsbegrenzung</div>
              <h2 className="font-display font-extrabold text-3xl tracking-tight mb-2">Bereits angemeldet</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Maximal <strong>1 Desktop + 1 Mobile</strong>. Wählen Sie eine Sitzung zum Beenden, um sich hier anzumelden.
              </p>
              <div className="space-y-2" data-testid="session-list">
                {activeSessions.map((s) => {
                  const Icon = s.device_type === "mobile" ? Smartphone : Monitor;
                  return (
                    <div key={s.jti} className="prime-card p-3 flex items-center gap-3" data-testid={`session-${s.jti}`}>
                      <Icon className="w-5 h-5 text-prime shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium uppercase tracking-wider">{s.device_type === "mobile" ? "Mobile" : "Desktop"}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{s.user_agent}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {fmtDate(s.last_seen_at)} · {s.ip || "—"}
                        </div>
                      </div>
                      <Button
                        type="button" size="sm" variant="outline"
                        onClick={() => terminateAndLogin(s.jti)}
                        disabled={loading}
                        data-testid={`terminate-${s.jti}`}
                        className="gap-1"
                      >
                        <X className="w-3 h-3" /> Beenden
                      </Button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => { setStep("credentials"); }}
                className="mt-4 w-full text-xs text-muted-foreground hover:text-foreground"
              >
                ← Abbrechen
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
